
import React, { useState } from 'react';
import { AppUser } from '../../../types';
import { XMarkIcon, SpinnerIcon, PhotoIcon, IdentificationIcon, UserCircleIcon } from '../../Icons';
import { useAuth } from '../../../hooks/useAuth';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../../constants';

interface IdentityFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  appUser: AppUser;
  onSuccess: () => void;
}

const TIPO_DOCS = [
    'Cédula de Ciudadanía',
    'Cédula de Extranjería',
    'Pasaporte',
    'Permiso Especial de Permanencia'
];

export const IdentityFormModal: React.FC<IdentityFormModalProps> = ({ isOpen, onClose, appUser, onSuccess }) => {
    const { updateAppUserProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        nombreCompleto: appUser.nombreCompleto || appUser.displayName || '',
        tipoIdentificacion: appUser.tipoIdentificacion || 'Cédula de Ciudadanía',
        numeroIdentificacion: appUser.numeroIdentificacion || '',
        photoURL: appUser.photoURL || ''
    });

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Simple validation
        if (!file.type.startsWith('image/')) {
            setError("Por favor selecciona un archivo de imagen válido.");
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setError("La imagen es demasiado pesada (Máximo 2MB).");
            return;
        }

        setLoading(true);
        try {
            // In this context, we'll use FileReader to convert to base64 for simplicity in this sandbox.
            // In production, we'd use Firebase Storage.
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, photoURL: reader.result as string }));
                setLoading(false);
            };
            reader.readAsDataURL(file);
        } catch (e) {
            setError("Error al procesar la imagen.");
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.nombreCompleto || !formData.numeroIdentificacion || !formData.photoURL) {
            setError("Todos los campos son obligatorios, incluyendo la foto.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await updateAppUserProfile(appUser.uid, {
                ...formData,
                carnetConfigurado: true
            });
            onSuccess();
        } catch (err) {
            setError("Error al guardar los datos. Inténtalo de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up">
                <header className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">Activa tu Identidad</h2>
                        <p className="text-xs text-sky-600 font-bold">Completa tus datos para generar tu carnet virtual</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6 text-gray-400" />
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium animate-shake">
                            {error}
                        </div>
                    )}

                    {/* Foto Upload */}
                    <div className="flex flex-col items-center">
                        <div className="relative group cursor-pointer">
                            <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-sky-100 bg-gray-100 flex items-center justify-center shadow-lg">
                                {formData.photoURL ? (
                                    <img src={formData.photoURL} className="w-full h-full object-cover" alt="Perfil" />
                                ) : (
                                    <UserCircleIcon className="w-16 h-16 text-gray-300" />
                                )}
                            </div>
                            <label className="absolute inset-0 flex items-center justify-center bg-black/40 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                <PhotoIcon className="w-6 h-6" />
                                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                            </label>
                        </div>
                        <p className="text-[10px] text-gray-400 font-black uppercase mt-2">Haz clic para subir tu foto</p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Nombre Completo</label>
                            <input 
                                type="text" 
                                value={formData.nombreCompleto} 
                                onChange={e => setFormData({...formData, nombreCompleto: e.target.value})}
                                className="w-full border-2 border-gray-100 rounded-xl p-3 text-sm font-bold focus:border-sky-500 outline-none transition-all"
                                placeholder="Escribe tu nombre oficial"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Tipo de Documento</label>
                                <select 
                                    value={formData.tipoIdentificacion}
                                    onChange={e => setFormData({...formData, tipoIdentificacion: e.target.value})}
                                    className="w-full border-2 border-gray-100 rounded-xl p-3 text-sm font-bold focus:border-sky-500 outline-none"
                                >
                                    {TIPO_DOCS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Número de Identificación</label>
                                <input 
                                    type="text" 
                                    value={formData.numeroIdentificacion} 
                                    onChange={e => setFormData({...formData, numeroIdentificacion: e.target.value})}
                                    className="w-full border-2 border-gray-100 rounded-xl p-3 text-sm font-bold focus:border-sky-500 outline-none transition-all"
                                    placeholder="1.000.000.000"
                                />
                            </div>
                        </div>
                    </div>

                    <footer className="pt-4 flex gap-3">
                        <button 
                            type="button" 
                            onClick={onClose}
                            className="flex-1 py-4 text-xs font-black uppercase text-gray-400 hover:text-gray-700 tracking-widest"
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="flex-[2] py-4 bg-gray-900 text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2"
                            style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                        >
                            {loading ? <SpinnerIcon className="w-5 h-5 animate-spin" /> : <IdentificationIcon className="w-5 h-5" />}
                            {loading ? 'Guardando...' : 'Generar Carnet'}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
};
